import { useMemo } from 'react';
import { getLastCertificationReservationConditions } from '@lp/features/AssignmentTables/utils';
import { EmployeeAssignmentCandidateMatch, EmployeeRole, LastCertificationsAtHubInfo } from '@/api/apis/resources';
import {
  getReservationConditions,
  getTargetTimeLabel,
  getTrialCertificationReservationConditions,
  perfect,
} from '../ReservationCondition/utils';
import { Unavailability } from '@/api/apis/resources/schedule';
import { EMPLOYEE_ROLE_NAMES } from '@/constants/user.constants';
import { useCaseDetails } from '@/hooks/useCaseDetails';
import { getTimeZonedDate } from '@/utils/helpers';
import { OrganType } from '@/api/apis/case';
import { TrialProgram } from '@/api/apis/facilities/trial-programs';
import { Uuid } from '@/api/types';

type ConfirmationConditionProps = {
  goTime?: string;
  match: EmployeeAssignmentCandidateMatch;
  onCallAfterTargetMinutes?: number | null;
  onCallBeforeTargetMinutes?: number | null;
  onRestEndsBeforeTargetMinutes?: number | null;
  onRestStartsAfterTargetMinutes?: number | null;
  unavailabilities?: Unavailability[];
  employeeRole: EmployeeRole.ClinicalSpecialist | EmployeeRole.RetrievalSurgeon;
  userName?: string;
  applyGoTime?: boolean;
  lastCertificationAtHubInfo?: LastCertificationsAtHubInfo;
  trialProgram?: TrialProgram | null;
  employeeTrialCertificates?: OrganType[];
  trialProgramIds?: Uuid[];
  hasTrialProgramCertification?: boolean;
};

export const ConfirmationCondition = ({
  goTime,
  match,
  onCallAfterTargetMinutes,
  onCallBeforeTargetMinutes,
  onRestEndsBeforeTargetMinutes,
  onRestStartsAfterTargetMinutes,
  unavailabilities,
  employeeRole,
  userName,
  applyGoTime,
  lastCertificationAtHubInfo,
  trialProgram,
  employeeTrialCertificates,
  trialProgramIds,
  hasTrialProgramCertification,
}: ConfirmationConditionProps) => {
  const { donorHospitalDate, donorHospitalTime, donorHospitalTimeZone } = useCaseDetails();
  const orTime = getTimeZonedDate(donorHospitalDate, donorHospitalTime, donorHospitalTimeZone)?.toAbsoluteString();

  const conditions = getReservationConditions(
    match,
    onCallBeforeTargetMinutes,
    onCallAfterTargetMinutes,
    onRestStartsAfterTargetMinutes,
    onRestEndsBeforeTargetMinutes,
    unavailabilities,
    orTime,
    applyGoTime,
  );

  const targetTime = applyGoTime ? goTime : orTime;
  const timeString = getTargetTimeLabel(applyGoTime, targetTime);
  const lastReservationExtras = getLastCertificationReservationConditions({
    lastCertificationAtHubInfo,
    withMayBecomeLast: true,
  });

  const computedHasTrialCert = useMemo(() => {
    if (hasTrialProgramCertification !== undefined) return hasTrialProgramCertification;
    if (!trialProgram) return true;

    const hasCertViaProgramOrgan =
      Boolean(trialProgram.organ) && Boolean(employeeTrialCertificates?.includes(trialProgram.organ as OrganType));
    const hasCertViaProgramId = Boolean(trialProgramIds?.includes(trialProgram.id));

    return hasCertViaProgramOrgan || hasCertViaProgramId;
  }, [hasTrialProgramCertification, trialProgram, employeeTrialCertificates, trialProgramIds]);

  const trialConditions = getTrialCertificationReservationConditions({
    isClinicalSpecialist: employeeRole === EmployeeRole.ClinicalSpecialist,
    csName: userName,
    trialProgram,
    hasTrialCertification: computedHasTrialCert,
  });

  const baseConditions = conditions.map((c) => c.replaceAll('Resource', ''));

  const showOnlyTrial =
    employeeRole === EmployeeRole.ClinicalSpecialist && perfect(match.matchType) && !computedHasTrialCert;

  const allConditions = showOnlyTrial
    ? trialConditions
    : [...baseConditions, ...trialConditions, ...lastReservationExtras];

  return (
    <div className="flex flex-col text-s-m">
      <div className="gap-1">
        <span className="text-s-sb">{EMPLOYEE_ROLE_NAMES[employeeRole]} </span>
        <span className="text-s-r">
          {userName}: {timeString} {allConditions.join(' ').replaceAll('Resource', '')}
        </span>
        <div className="text-s-r">Are you sure you want to proceed?</div>
      </div>
    </div>
  );
};
